"use client";

import React from "react";
import Link from "next/link";
import {
  Building2,
  MapPin,
  FileText,
  Phone,
  ArrowRight,
  Filter,
  Loader2,
  AlertCircle,
  Building,
  Layers,
} from "lucide-react";
import { useFpoConnect } from "@/modules/public/home/hooks/usePublicData";

export default function FpoConnectClient() {
  const {
    stocks,
    loading,
    error,
    selectedDistrict,
    setSelectedDistrict,
    selectedCategory,
    setSelectedCategory,
    groupedFpos,
    uniqueDistricts,
    uniqueCategories,
    totalUniqueFposCount,
    reload: fetchApprovedStocks,
  } = useFpoConnect();

  return (
    <div className="space-y-12">
      {/* 2. Interactive Filter Controls */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-xs">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            {/* District Filter */}
            <div className="relative min-w-[200px] flex-1 sm:flex-none">
              <label htmlFor="district-filter" className="sr-only">Filter by District</label>
              <Filter className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <select
                id="district-filter"
                value={selectedDistrict}
                onChange={(e) => setSelectedDistrict(e.target.value)}
                className="w-full pl-10 pr-8 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 font-semibold focus:bg-white focus:ring-2 focus:ring-green-500/10 focus:border-green-550 outline-hidden transition duration-200 cursor-pointer appearance-none"
              >
                <option value="">All Districts</option>
                {uniqueDistricts.map((district) => (
                  <option key={district} value={district}>
                    {district}
                  </option>
                ))}
              </select>
            </div>

            {/* Category Filter */}
            <div className="relative min-w-[200px] flex-1 sm:flex-none">
              <label htmlFor="category-filter" className="sr-only">Filter by Category</label>
              <Layers className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <select
                id="category-filter"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full pl-10 pr-8 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 font-semibold focus:bg-white focus:ring-2 focus:ring-green-500/10 focus:border-green-550 outline-hidden transition duration-200 cursor-pointer appearance-none"
              >
                <option value="">All Categories</option>
                {uniqueCategories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            {/* Reset Filters */}
            {(selectedDistrict || selectedCategory) && (
              <button
                onClick={() => {
                  setSelectedDistrict("");
                  setSelectedCategory("");
                }}
                className="px-5 py-3 text-sm font-bold text-green-700 hover:text-green-800 bg-green-50 hover:bg-green-100 rounded-lg transition duration-200 shrink-0 text-center cursor-pointer border border-green-100"
              >
                Clear All
              </button>
            )}
          </div>

          {/* Filter Summary */}
          {stocks.length > 0 && (
            <p className="text-xs text-gray-500 font-medium mt-3.5 ml-0.5">
              Showing {groupedFpos.length} of {totalUniqueFposCount} registered Farmer Producer Organizations
            </p>
          )}
        </div>
      </section>

      {/* 3. FPO Cards Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {loading ? (
          <div className="min-h-[300px] flex flex-col items-center justify-center bg-white rounded-sm border border-gray-200 shadow-xs">
            <Loader2 className="w-10 h-10 text-green-600 animate-spin mb-3" />
            <p className="text-gray-500 font-bold text-sm">Loading FPO Directory...</p>
          </div>
        ) : error ? (
          <div className="min-h-[300px] flex flex-col items-center justify-center p-8 bg-white rounded-sm border border-gray-200 shadow-xs text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mb-3" />
            <h4 className="font-bold text-gray-800 text-lg mb-1">Failed to load profiles</h4>
            <p className="text-sm text-gray-500 max-w-md mb-4">{error}</p>
            <button
              onClick={fetchApprovedStocks}
              className="bg-green-600 hover:bg-green-700 text-white font-bold px-5 py-2 rounded-sm text-sm transition"
            >
              Retry
            </button>
          </div>
        ) : groupedFpos.length === 0 ? (
          <div className="min-h-[300px] flex flex-col items-center justify-center p-12 bg-white rounded-xl border border-gray-200 shadow-xs text-center">
            <Building className="w-14 h-14 text-gray-300 mb-3.5" />
            <h4 className="font-bold text-gray-800 text-lg mb-1">No FPOs match your filters</h4>
            <p className="text-sm text-gray-500 max-w-md">
              Try adjusting your keywords, selecting a different district/category, or resetting all filters.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {groupedFpos.map((fpo) => (
              <div
                key={fpo.fpoUserId}
                className="bg-white rounded-sm border-l-4 border-l-[#0f9648] border-y border-r border-gray-250 hover:border-gray-300 hover:shadow-md transition-all duration-300 flex flex-col justify-between h-full group relative overflow-hidden shadow-xs"
              >
                {/* Upper Details */}
                <div className="p-4 space-y-3">
                  <div>
                    <span className="font-extrabold text-[#0f9648] block text-[9px] uppercase tracking-wider mb-0.5">Farmer Producer Org</span>
                    <h3 className="text-gray-950 font-bold text-base tracking-tight leading-snug group-hover:text-[#0f9648] transition-colors line-clamp-1">
                      {fpo.fpoName}
                    </h3>
                  </div>

                  <div className="border-t border-gray-100 pt-3 space-y-2.5 text-xs font-semibold">
                    {/* FPO Contact Number */}
                    <div className="flex items-start gap-2.5">
                      <div className="w-7 h-7 rounded-sm bg-green-50 flex items-center justify-center shrink-0 border border-green-100/50">
                        <Phone className="w-3.5 h-3.5 text-[#0f9648]" />
                      </div>
                      <div className="space-y-0.5">
                        <span className="font-bold text-gray-500 block text-[9px] uppercase tracking-wider">FPO Contact Number</span>
                        {fpo.mobileNumber ? (
                          <a href={`tel:${fpo.mobileNumber}`} className="text-gray-900 font-bold text-xs sm:text-sm hover:text-[#0f9648] transition-colors">
                            {fpo.mobileNumber}
                          </a>
                        ) : (
                          <span className="text-gray-550 font-medium text-xs">—</span>
                        )}
                      </div>
                    </div>

                    {/* GST Number */}
                    <div className="flex items-start gap-2.5">
                      <div className="w-7 h-7 rounded-sm bg-blue-50 flex items-center justify-center shrink-0 border border-blue-100/50">
                        <FileText className="w-3.5 h-3.5 text-blue-600" />
                      </div>
                      <div className="space-y-0.5">
                        <span className="font-bold text-gray-500 block text-[9px] uppercase tracking-wider">GST Number</span>
                        <span className="text-gray-900 font-bold text-xs uppercase">
                          {fpo.gstNumber || "Not Provided"}
                        </span>
                      </div>
                    </div>

                    {/* Address Details */}
                    <div className="flex items-start gap-2.5">
                      <div className="w-7 h-7 rounded-sm bg-red-50 flex items-center justify-center shrink-0 border border-red-100/50">
                        <MapPin className="w-3.5 h-3.5 text-red-500" />
                      </div>
                      <div className="space-y-0.5 w-full min-w-0">
                        <span className="font-bold text-gray-550 block text-[9px] uppercase tracking-wider font-semibold mb-0.5">Address Details</span>
                        <span 
                          className="text-gray-800 font-semibold text-xs leading-normal line-clamp-1 block truncate" 
                          title={fpo.address ? `${fpo.address}, ${[fpo.taluk, fpo.district].filter(Boolean).map(x => typeof x === 'object' ? x.name : x).join(", ")}` : [fpo.taluk, fpo.district].filter(Boolean).map(x => typeof x === 'object' ? x.name : x).join(", ")}
                        >
                          {fpo.address ? (
                            `${fpo.address}, ${[fpo.taluk, fpo.district].filter(Boolean).map(x => typeof x === 'object' ? x.name : x).join(", ")}`
                          ) : (
                            [fpo.taluk, fpo.district].filter(Boolean).map(x => typeof x === 'object' ? x.name : x).join(", ") || "Karnataka, India"
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer Action */}
                <div className="p-3 bg-gray-50 border-t border-gray-150 rounded-b-sm">
                  <Link
                    href={`/fpo-connect/${fpo.slug || fpo.fpoUserId}`}
                    className="w-full flex items-center justify-center gap-1.5 py-2.5 bg-[#0f9648] hover:bg-[#0b6330] text-white rounded-sm text-xs font-bold transition-all duration-300 cursor-pointer shadow-xs hover:shadow-sm"
                  >
                    View Directory & Requirements
                    <ArrowRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
